import type { Room } from 'livekit-client'
import { getAudioSentryPort, type AudioSentryPort } from './audioSentryPort'

/**
 * speechReachabilitySink — the durable record of a client whose speech is not
 * reaching the SFU.
 *
 * The GA row (`meeting_speech_reachability_problem_escalated`) says a client
 * escalated and when. This says which client, in which room, on which
 * microphone track — the fields an incident actually needs, which GA cannot
 * carry queryably and which are gone by the time anyone reads the room.
 *
 * `micTrackSid` is the one that matters most. It is the client's own name for
 * the audio track it believes it published, so it is the join key against the
 * server: a LiveKit `track_published` webhook, an egress, a transcriber log.
 * When the fault is real, that sid appears nowhere on the server side, and
 * having it recorded at the moment the client noticed is the difference between
 * proving that in minutes and reconstructing it from analytics after the class
 * is over.
 *
 * Sentry rather than Firestore because this is diagnostics, not session state:
 * it is the same durable store the dead-air episodes already write to, reached
 * through the same injected port (see audioSentryPort for why the port exists).
 * With no port registered - tests, non-web apps - the write is skipped and the
 * GA row still stands on its own.
 *
 * No sampler. A dead-air episode repeats every scan for as long as a peer stays
 * dead, so it needs a min-interval gate; an escalation is already the rare end
 * of one, raised at most once per wait duration and only after a run of
 * unconfirmed utterances. Gating it again would drop the second incident in a
 * session rather than the noise.
 */

export interface SpeechReachabilityEscalation {
  /** The client whose speech the server is not registering. */
  listenerId: string
  roomId: string
  /** How long speech has gone unconfirmed, measured from the run's first verdict. */
  unreachableForMs: number
  /** Unconfirmed utterances in this run, including the one that escalated. */
  unconfirmedUtterances: number
  /** Slide the client was on when it escalated. */
  activeSlide?: number
}

export interface SpeechReachabilitySinkOptions {
  escalation: SpeechReachabilityEscalation
  /**
   * The live room, read for the microphone publication and connection state.
   * Every read is guarded, so a disconnected room or an SDK shape change
   * degrades to `undefined` fields rather than throwing.
   */
  room?: Room | null
  /** Lazy Sentry-port accessor (defaults to the registered global port). */
  sentry?: () => AudioSentryPort | null
}

/** What the client believes it published, as the record reads it. */
interface MicSnapshot {
  trackSid?: string
  muted?: boolean
  published: boolean
}

function guarded<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch {
    return undefined
  }
}

/**
 * The local microphone publication, preferred by source.
 *
 * A client can publish more than one audio track (screen-share audio), and the
 * mic is the only one this record is about - naming a screen-share sid would
 * send an investigation looking for the wrong track on the server.
 */
function readMic(room?: Room | null): MicSnapshot {
  const pubs = guarded(() =>
    Array.from(room?.localParticipant?.audioTrackPublications?.values() ?? [])
  )
  if (!pubs || pubs.length === 0) return { published: false }
  const mic = pubs.find((p) => guarded(() => p.source) === 'microphone')
  const pub = mic ?? pubs[0]
  return {
    trackSid: guarded(() => pub?.trackSid),
    muted: guarded(() => pub?.isMuted),
    published: true,
  }
}

/**
 * Write one escalation to the durable sink.
 *
 * Guarded end to end. This runs from a meeting-page event handler in the middle
 * of a session that is already going wrong for the user; a throw from the
 * Sentry port must not be what stops the dialog from opening.
 */
export function captureSpeechReachabilityEscalation({
  escalation,
  room,
  sentry = getAudioSentryPort,
}: SpeechReachabilitySinkOptions): void {
  const port = guarded(sentry)
  if (!port) return

  const mic = readMic(room)
  const connectionState = guarded(() => room?.state)

  guarded(() =>
    port.captureMessage({
      message: 'meeting_speech_unreachable',
      level: 'warning',
      tags: {
        listener: escalation.listenerId,
        room: escalation.roomId,
        unreachable_for_ms: escalation.unreachableForMs,
        unconfirmed_utterances: escalation.unconfirmedUtterances,
        // Defaulted rather than omitted: a missing Sentry tag cannot be
        // searched for, and "we could not read it" is itself a finding on a
        // client that thinks it is publishing.
        mic_published: mic.published,
        mic_track_sid: mic.trackSid ?? 'unknown',
        mic_muted: mic.muted ?? 'unknown',
        connection_state: connectionState ?? 'unknown',
        active_slide: escalation.activeSlide ?? 'unknown',
      },
      contexts: {
        speechReachability: {
          ...escalation,
          micTrackSid: mic.trackSid,
          micPublished: mic.published,
          micMuted: mic.muted,
          connectionState,
        },
      },
      // One issue for the fault, not one per room: every escalation is the same
      // failure, and the room is a tag.
      fingerprint: ['meeting_speech_unreachable'],
    })
  )
}
